import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  CircularProgress,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import PrevPageIcon from "@mui/icons-material/NavigateBefore";
import NextPageIcon from "@mui/icons-material/NavigateNext";
import LastPageIcon from "@mui/icons-material/LastPage";
import moment from "moment";
import { Tooltip, IconButton as MuiIconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import TextField from "./TextField";
import MenuItem from "./MenuItem";

const rowsPerPageOptions = [5, 10, 15, 20, 25, 50, 100, 500];

const getGroupPlate = (group) => group?.plateNumber || group?.plateNo || "-";
const getGroupContract = (group) => group?.contractNumber || group?.cn || "-";

const getGroupCount = (group) =>
  group?.count !== undefined && group?.count !== null ? group.count : "-";

const getGroupTotalPotential = (group) =>
  group?.totalPotential !== undefined && group?.totalPotential !== null
    ? group.totalPotential
    : "-";

const renderCellValue = (row, column, translate) => {
  const value = row?.[column.id];

  if (column.id === "completed") {
    return value
      ? translate("Completed") || "Completed"
      : translate("Not Completed") || "Not Completed";
  }

  if (column.id === "categoryName") {
    return translate(value) || value || "";
  }

  if (
    column.id === "odometerReading" ||
    column.id === "businessPotential" ||
    column.id === "nextOdometerReading"
  ) {
    return value !== undefined && value !== null ? value : "-";
  }

  if (column.id === "lastServiceDate") {
    return value ? moment(value).format("DD/MM/YYYY") : "-";
  }

  if (column.id === "nextExecutionDate") {
    if (!value) return "-";
    const daysDiff = moment.utc(value).diff(moment(), "days");
    return (
      <span style={{ color: daysDiff >= 0 ? "inherit" : "red" }}>
        {moment(value).format("DD/MM/YYYY")}
      </span>
    );
  }

  if (column.id === "completedAt") {
    return value ? moment(value).format("DD/MM/YYYY") : "-";
  }

  return value ?? "-";
};

export default function PmsGroupedTable({
  rows = [],
  childRowDefinition = [],
  translate = (key) => key,
  pagination = {},
  expandedGroupKeys = [],
  expandedGroups = {},
  onToggleGroup,
  onGroupPageChange,
  onPageChange,
  onRowsPerPageChange,
  onAddBooking,
  onCreateCrc,
}) {
  const showActions =
    typeof onAddBooking === "function" || typeof onCreateCrc === "function";
  const childColumns = [
    ...childRowDefinition,
    ...(showActions
      ? [
          {
            id: "__actions",
            label: translate("actions") || "Actions",
            isAction: true,
          },
        ]
      : []),
  ];
  const isExpanded = (group) =>
    expandedGroupKeys.includes(
      `${getGroupContract(group)}__${getGroupPlate(group)}`,
    );

  const getGroupKey = (group) =>
    `${getGroupContract(group)}__${getGroupPlate(group)}`;

  return (
    <Paper style={{ width: "100%", overflow: "hidden" }}>
      <div style={{ overflowX: "auto" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 48, padding: "0 10px" }} />
              <TableCell style={{ padding: "0 10px" }}>
                {translate("plateNumber")}
              </TableCell>
              <TableCell style={{ padding: "0 10px" }}>
                {translate("contractNumber")}
              </TableCell>
              <TableCell style={{ padding: "0 10px" }}>
                {translate("count")}
              </TableCell>
              <TableCell style={{ padding: "0 10px" }}>
                {translate("totalPotential")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length ? (
              rows.map((group, index) => {
                const groupKey = getGroupKey(group);
                const expanded = isExpanded(group);
                const groupState = expandedGroups[groupKey] || {};
                const childRows = groupState.items || [];

                return (
                  <React.Fragment key={`${groupKey}-${index}`}>
                    <TableRow
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => onToggleGroup(group)}
                    >
                      <TableCell style={{ padding: "0 10px" }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleGroup(group);
                          }}
                        >
                          {expanded ? (
                            <KeyboardArrowDownIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowRightIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell style={{ padding: "0 10px", fontWeight: 600 }}>
                        {getGroupPlate(group)}
                      </TableCell>
                      <TableCell style={{ padding: "0 10px" }}>
                        {getGroupContract(group)}
                      </TableCell>
                      <TableCell style={{ padding: "0 10px" }}>
                        {getGroupCount(group)}
                      </TableCell>
                      <TableCell style={{ padding: "0 10px" }}>
                        {getGroupTotalPotential(group)}
                      </TableCell>
                    </TableRow>

                    {expanded && (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          style={{ padding: 0, borderBottom: "none" }}
                        >
                          <Box sx={{ backgroundColor: "action.hover", p: 1 }}>
                            {groupState.loading ? (
                              <Box
                                display="flex"
                                justifyContent="center"
                                py={2}
                              >
                                <CircularProgress size={24} />
                              </Box>
                            ) : childRows.length ? (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    {childColumns.map((column) => (
                                      <TableCell
                                        key={column.id}
                                        style={{ padding: "0 10px" }}
                                      >
                                        {column.label}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {childRows.map((child, childIndex) => (
                                    <TableRow key={child.id || childIndex}>
                                      {childColumns.map((column) => (
                                        <TableCell
                                          key={`${child.id || childIndex}-${column.id}`}
                                          style={{
                                            padding: "0 10px",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {column.isAction ? (
                                            <Box
                                              display="inline-flex"
                                              alignItems="center"
                                              gap={0.5}
                                            >
                                              {typeof onAddBooking ===
                                                "function" && (
                                                <Tooltip
                                                  title={
                                                    translate("addBooking") ||
                                                    "Add Booking"
                                                  }
                                                >
                                                  <span>
                                                    <MuiIconButton
                                                      size="small"
                                                      color="primary"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        onAddBooking(
                                                          child,
                                                          group,
                                                        );
                                                      }}
                                                    >
                                                      <AddIcon fontSize="small" />
                                                    </MuiIconButton>
                                                  </span>
                                                </Tooltip>
                                              )}
                                              {typeof onCreateCrc ===
                                                "function" && (
                                                <Tooltip
                                                  title={
                                                    translate("createCrc") ||
                                                    "Create CRC"
                                                  }
                                                >
                                                  <span>
                                                    <MuiIconButton
                                                      size="small"
                                                      color="primary"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        onCreateCrc(
                                                          child,
                                                          group,
                                                        );
                                                      }}
                                                    >
                                                      <DriveFileRenameOutlineIcon fontSize="small" />
                                                    </MuiIconButton>
                                                  </span>
                                                </Tooltip>
                                              )}
                                            </Box>
                                          ) : (
                                            renderCellValue(
                                              child,
                                              column,
                                              translate,
                                            )
                                          )}
                                        </TableCell>
                                      ))}
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            ) : (
                              <Box py={2} textAlign="center">
                                {translate("noRecordFound")}
                              </Box>
                            )}

                            {groupState.total > 0 && (
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="flex-end"
                                gap={1}
                                py={1}
                                px={1}
                              >
                                <span style={{ fontSize: 12 }}>
                                  {translate("page")}{" "}
                                  <strong>{groupState.page || 1}</strong> /{" "}
                                  {groupState.lastPage || 1}
                                </span>
                                <IconButton
                                  size="small"
                                  disabled={(groupState.page || 1) <= 1}
                                  onClick={() =>
                                    onGroupPageChange(group, "first")
                                  }
                                >
                                  <FirstPageIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  disabled={(groupState.page || 1) <= 1}
                                  onClick={() =>
                                    onGroupPageChange(group, "prev")
                                  }
                                >
                                  <PrevPageIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  disabled={!groupState.hasNext}
                                  onClick={() =>
                                    onGroupPageChange(group, "next")
                                  }
                                >
                                  <NextPageIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  disabled={
                                    (groupState.page || 1) >=
                                    (groupState.lastPage || 1)
                                  }
                                  onClick={() =>
                                    onGroupPageChange(group, "last")
                                  }
                                >
                                  <LastPageIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={5} style={{ padding: "0 10px" }}>
                  <h3 style={{ display: "flex", justifyContent: "center" }}>
                    {translate("noRecordFound")}
                  </h3>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div
        style={{
          padding: "0 20px",
          display: "flex",
          alignItems: "center",
          color: "inherit",
        }}
      >
        <span style={{ marginRight: 10 }} className="hidden-responsive">
          {translate("rowsPerPage")}
        </span>
        <span style={{ marginRight: 10, minWidth: 60 }}>
          <TextField
            id="rowsPerPage"
            select
            style={{ width: 60 }}
            value={pagination.pageSize || 50}
            onChange={(e) => onRowsPerPageChange(e.target.value)}
            margin="dense"
          >
            {rowsPerPageOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ marginRight: 5, whiteSpace: "nowrap" }}>
          {translate("page")} <strong>{pagination.page || 1}</strong>
        </span>
        <span style={{ marginRight: 10 }} className="hidden-responsive">
          {translate("outOf")} <strong>{pagination.lastPage || 1}</strong>
        </span>
        <span style={{ marginRight: 10 }} className="hidden-responsive">
          {translate("totalRecords")} <strong>{pagination.total || 0}</strong>
        </span>
        <span style={{ marginRight: 10, cursor: "pointer" }}>
          <FirstPageIcon
            fontSize="small"
            onClick={() => onPageChange("first")}
          />
        </span>
        <span style={{ marginRight: 10, cursor: "pointer" }}>
          <PrevPageIcon fontSize="small" onClick={() => onPageChange("prev")} />
        </span>
        <span style={{ marginRight: 10, cursor: "pointer" }}>
          <NextPageIcon fontSize="small" onClick={() => onPageChange("next")} />
        </span>
        <span style={{ marginRight: 10, cursor: "pointer" }}>
          <LastPageIcon fontSize="small" onClick={() => onPageChange("last")} />
        </span>
      </div>
    </Paper>
  );
}
